import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useUpdateVendorSchedule } from "hooks/useVendors";
import { api } from "api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Ring } from "@uiball/loaders";
import { Toast } from "primereact/toast";
import { toHHmm } from "util/time";
import TimeField from "components/Ui/TimeField/TimeField";
import { WEEKDAY_OPTIONS } from "utils/dateFormat";
import "components/VendorTimes/vendorTimes.scss";
// The weekday names come from the one list in utils/dateFormat.js.
const DAYS = WEEKDAY_OPTIONS;

export default function VendorScheduleSingleModal({ show, vendorId, onClose }) {
    const [schedule, setSchedule] = useState({});
    const updateVendorScheduleMutation = useUpdateVendorSchedule();
    const [isLoading, setLoading] = useState(false);
    const toast = useRef(null);

    useEffect(() => {
        if (!vendorId || !show) return;

        setLoading(true);
        api.get(`vendor/weekly-schedule/${vendorId}`)
            .then((res) => {
                const data = {};
                res.data.schedule.forEach((d) => {
                    if (!d.openTime && !d.closeTime && !d.isClosed) return;
                    data[d.day] = {
                        openTime: toHHmm(d.openTime),
                        closeTime: toHHmm(d.closeTime),
                        isClosed: d.isClosed,
                    };
                });
                setSchedule(data);
            })
            .finally(() => setLoading(false));
    }, [vendorId, show]);

    const updateDay = (day, field, value) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value,
                isClosed: field === "isClosed" ? value : false,
                remove: false,
            },
        }));
    };

    /** Mark a day for removal (deletes DB row → admin-managed) */
    const removeDay = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: { remove: true },
        }));
    };

    /** Re-add a removed day with empty times */
    const addDay = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: { openTime: "", closeTime: "", isClosed: false, remove: false },
        }));
    };

    const save = async () => {
        try {
            await updateVendorScheduleMutation.mutateAsync({ id: vendorId, data: { schedule } });
            onClose();
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "خطأ",
                detail: err.message,
                life: 4000,
            });
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header style={{ display: "flex", justifyContent: "space-between" }}>
                <Modal.Title>مواعيد العمل</Modal.Title>
                <FontAwesomeIcon icon={faClose} onClick={onClose} size="lg" cursor="pointer" />
            </Modal.Header>
            {updateVendorScheduleMutation.isPending ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : null}
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            <Modal.Body style={{ padding: "12px 16px" }}>
                {isLoading
                    ? "جاري التحميل..."
                    : DAYS.map((day, idx) => {
                          const entry = schedule[day.key];
                          const isRemoved = !entry || entry.remove;
                          return (
                              <div
                                  key={day.key}
                                  // Painted by classes, not inline, so the dark rules reach
                                  // the row (see VendorTimes/vendorTimes.scss).
                                  className={`vendor-times__row ${
                                      idx % 2 === 0 ? "surface-inset" : "surface-card"
                                  }`}
                              >
                                  <strong className="vendor-times__day">{day.label}</strong>

                                  <div className="vendor-times__divider" />
                                  {isRemoved ? (
                                      <>
                                          <span
                                              className="text-muted"
                                              style={{
                                                  flex: 1,
                                                  fontStyle: "italic",
                                                  fontSize: "0.875rem",
                                                  paddingRight: "4px",
                                              }}
                                          >
                                              لم يتم التعيين
                                          </span>
                                          <Button
                                              variant="outline-success"
                                              size="sm"
                                              onClick={() => addDay(day.key)}
                                              title="تعيين أوقات"
                                              style={{ flexShrink: 0 }}
                                          >
                                              <FontAwesomeIcon icon={faPlus} className="me-1" />{" "}
                                              تعيين
                                          </Button>
                                      </>
                                  ) : (
                                      <>
                                          <TimeField
                                              disabled={entry?.isClosed}
                                              value={entry?.openTime || ""}
                                              onChange={(value) =>
                                                  updateDay(day.key, "openTime", value)
                                              }
                                          />

                                          <span
                                              style={{
                                                  flexShrink: 0,
                                                  fontSize: "0.875rem",
                                              }}
                                          >
                                              إلى
                                          </span>

                                          <TimeField
                                              disabled={entry?.isClosed}
                                              value={entry?.closeTime || ""}
                                              onChange={(value) =>
                                                  updateDay(day.key, "closeTime", value)
                                              }
                                          />

                                          <label
                                              style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "6px",
                                                  flexShrink: 0,
                                                  marginBottom: 0,
                                                  cursor: "pointer",
                                                  userSelect: "none",
                                                  fontSize: "0.875rem",
                                                  ...(entry?.isClosed ? { color: BRAND } : {}),
                                                  fontWeight: entry?.isClosed ? 600 : 400,
                                              }}
                                          >
                                              <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  style={{ marginTop: 0, cursor: "pointer" }}
                                                  checked={entry?.isClosed || false}
                                                  onChange={(e) =>
                                                      updateDay(
                                                          day.key,
                                                          "isClosed",
                                                          e.target.checked,
                                                      )
                                                  }
                                              />
                                              مغلق
                                          </label>
                                          <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => removeDay(day.key)}
                                              title="إزالة الأوقات"
                                              style={{ flexShrink: 0, marginRight: "auto" }}
                                          >
                                              <FontAwesomeIcon icon={faTrash} />
                                          </Button>
                                      </>
                                  )}
                              </div>
                          );
                      })}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    إلغاء
                </Button>
                <Button variant="success" onClick={save}>
                    حفظ
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
